import * as React from 'react'
import { useSession, signOut } from 'next-auth/react'
import Link from 'next/link'

import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded'
import BeenhereRoundedIcon from '@mui/icons-material/BeenhereRounded'
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'

import { I18n, useTranslation } from 'components/hooks/use-translation'
import { useUser } from 'components/hooks/state'
import IconButton from 'components/atomic/atoms/icon-button'
import Button from 'components/atomic/atoms/button'
import Drawer from 'components/atomic/atoms/drawer'
import Avatar from 'components/atomic/atoms/avatar'
import Chip from 'components/atomic/atoms/chip'
import Text from 'components/atomic/atoms/text'
import PolicyRoundedIcon from '@mui/icons-material/PolicyRounded'
import SubjectRoundedIcon from '@mui/icons-material/SubjectRounded'
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded'

import Routes from 'utils/routes'
import Logo from 'components/atomic/atoms/logo'

const AuthSection = ({ onClose }: { onClose: () => void }) => {
    const { t }: I18n = useTranslation()
    const { user } = useUser()

    const isFreeUser = React.useMemo(() => (!user ? false : user.subscription_status !== 'ACTIVE'), [user])
    const hasDoneQuiz = React.useMemo(() => (!user ? false : !!user.personalized_path), [user])

    return (
        <>
            <Stack spacing={1}>
                <Stack mb={1} direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Badge
                            overlap="circular"
                            color="success"
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            variant="dot">
                            <Avatar
                                alt=""
                                src={user?.avatar || undefined}
                                sizes=""
                                sx={{
                                    width: 24,
                                    height: 24,
                                    border: (theme) => `1px solid ${theme.palette.divider}`,
                                }}
                            />
                        </Badge>
                        <Text>{user?.first_name}</Text>
                        <Link href={Routes.AccountSettings} passHref>
                            <a>
                                <Chip label={t(`common:plans.${isFreeUser ? 'starter' : 'pro'}`)} size="small" />
                            </a>
                        </Link>
                    </Stack>
                    <IconButton aria-label="Close" onClick={onClose}>
                        <CloseRoundedIcon fontSize="small" />
                    </IconButton>
                </Stack>

                <Link href={Routes.AccountSettings} passHref>
                    <a>
                        <Button
                            fullWidth
                            color="inherit"
                            sx={{ justifyContent: 'start' }}
                            startIcon={<ManageAccountsRoundedIcon />}>
                            {t('common:header.accountSettings')}
                        </Button>
                    </a>
                </Link>

                <Link href={Routes.PlanSettings} passHref>
                    <a>
                        <Button
                            fullWidth
                            color="inherit"
                            sx={{ justifyContent: 'start' }}
                            startIcon={<BeenhereRoundedIcon />}>
                            {t('common:header.planSettings')}
                        </Button>
                    </a>
                </Link>
            </Stack>

            {hasDoneQuiz ? (
                <Link href={isFreeUser ? Routes.SortingQuizResults : Routes.MyPath} passHref>
                    <a>
                        <Button fullWidth variant="outlined">
                            {t('common:header.myPath')}
                        </Button>
                    </a>
                </Link>
            ) : (
                <Link href={Routes.SortingQuiz} passHref>
                    <a>
                        <Button fullWidth variant="outlined">
                            {t('common:header.takeSortingQuiz')}
                        </Button>
                    </a>
                </Link>
            )}

            {isFreeUser ? (
                <Link href={Routes.Pricing} passHref>
                    <a>
                        <Button fullWidth variant="contained">
                            {t('common:header.upgrade')}
                        </Button>
                    </a>
                </Link>
            ) : null}

            <Box>
                <Divider orientation="horizontal" />
            </Box>

            <Button
                sx={{ color: 'text.secondary', justifyContent: 'start' }}
                startIcon={<LogoutRoundedIcon />}
                onClick={() => signOut()}>
                {t('common:buttons.logout')}
            </Button>
        </>
    )
}

const UnAuthSection = ({ onClose }: { onClose: () => void }) => {
    const { t }: I18n = useTranslation()

    return (
        <Stack spacing={3}>
            {/* <SupportIcon /> */}

            <Stack direction="column" spacing={2}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent={'space-between'}>
                    <Logo clickable={true} link={Routes.Home} />
                    <IconButton aria-label="close menu" variant={'outlined'} onClick={onClose}>
                        <CloseRoundedIcon fontSize="small" />
                    </IconButton>
                </Stack>
                <Text variant="caption">{t('common:footer.description')}</Text>
            </Stack>

            <Stack spacing={1}>
                <Link href={Routes.SignUp}>
                    <a>
                        <Button fullWidth variant="contained">
                            {t('common:header.signup')}
                        </Button>
                    </a>
                </Link>
                <Link href={Routes.SignIn}>
                    <a>
                        <Button fullWidth variant="outlined">
                            {t('common:header.login')}
                        </Button>
                    </a>
                </Link>
            </Stack>

            <Box>
                <Divider orientation="horizontal" />
            </Box>

            <Stack spacing={1}>
                <Link href={Routes.Catalog} passHref>
                    <a>
                        <Button
                            fullWidth
                            color="inherit"
                            sx={{ justifyContent: 'start' }}
                            startIcon={<SubjectRoundedIcon />}>
                            Catalogo dei corsi
                        </Button>
                    </a>
                </Link>

                <Link href={Routes.WikiTools} passHref>
                    <a>
                        <Button
                            fullWidth
                            color="inherit"
                            sx={{ justifyContent: 'start' }}
                            startIcon={<SchoolRoundedIcon />}>
                            Wiki della programmazione
                        </Button>
                    </a>
                </Link>

                <Link href={Routes.SortingQuiz} passHref>
                    <a>
                        <Button
                            fullWidth
                            color="inherit"
                            sx={{ justifyContent: 'start' }}
                            startIcon={<PolicyRoundedIcon />}>
                            {t('common:header.takeSortingQuiz')}
                        </Button>
                    </a>
                </Link>
            </Stack>
        </Stack>
    )
}

const MobileDrawer = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
    const { status } = useSession()

    return (
        <Drawer anchor="right" open={open} onClose={onClose}>
            <Stack spacing={2} p={3}>
                {status === 'authenticated' ? <AuthSection onClose={onClose} /> : <UnAuthSection onClose={onClose} />}
            </Stack>
        </Drawer>
    )
}

export default MobileDrawer
